import FormHelperText, {
  type FormHelperTextProps,
} from "@mui/material/FormHelperText";
import { useListFormatter } from "core/hooks/useListFormatter";
import {
  type ArrayPath,
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

type TranslationFieldErrorMessageProps<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  name: TName;
  translationFieldLocaleKey: keyof TFieldValues[TName][number];
} & FormHelperTextProps;

export function TranslationFieldErrorMessage<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues>,
>({
  control,
  name,
  translationFieldLocaleKey,
  ...props
}: TranslationFieldErrorMessageProps<TFieldValues, TName>) {
  const listFormatter = useListFormatter();
  const {
    field: { value: translations },
    formState: { errors },
  } = useController({ name: name as FieldPath<TFieldValues>, control });
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:TranslationFieldErrorMessage",
  });
  const { t: tLocale } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:LanguageCodes",
  });

  const translationErrors =
    (Array.isArray(errors.translations) &&
      errors.translations.reduce((acc, error, index) => {
        const translationEntry = translations.at(index);
        if (!translationEntry || !error) {
          return acc;
        }
        const translationLanguage = tLocale(
          translationEntry[translationFieldLocaleKey]
        );
        acc.push(translationLanguage);
        return acc;
      }, [] as string[])) ||
    [];

  const errorLanguages = listFormatter.format(translationErrors);
  if (translationErrors.length === 0) {
    return null;
  }
  return (
    <FormHelperText data-testid="translation-field-error-text" error {...props}>
      {t("translationsError", {
        locales: errorLanguages,
        count: translationErrors.length,
      })}
    </FormHelperText>
  );
}
